/**
 * Block 13 PR-G Phase 4A — Contract form container (ADR-0005).
 *
 * Orchestrates:
 *   - URL guard: optional `:contractId` param (Phase 6 router wiring). Без param
 *     генерируем new UUID (genUuid()) и стартуем blank.
 *   - Template key derivation: useAuthStore().region → contractRegion → default
 *     lang per region. Fallback 'KZ' если auth не загружен.
 *   - Hydration cascade:
 *       1. Zustand `useContractDraftStore` (sync, fast cross-route cache).
 *       2. IDB `loadContractDraftFromIdb` (durable cross-session).
 *       3. `useFromQuoteSeeding` seed (если URL `?fromQuote=<uuid>`).
 *       4. Blank `createInitialContractDraft`.
 *   - Autosave (300ms debounce) — mirror Block 12 pattern. Skip pristine state
 *     (защитный гард — нет смысла писать blank).
 *
 * **clearDraft** Phase 4A — выполняется в `BottomActions.handleSave`
 *   (mirror Block 12 BottomActionButtons pattern). Container не наблюдает success
 *   directly — autosave продолжит до user-action save.
 *
 * **NO router wiring** (Phase 6) — Container assumes consumer mounted on route;
 * Phase 4A не trogает `apps/web/src/router.tsx`.
 *
 * Reference:
 *   - `features/calculator/quote/QuotePageContainer.tsx` (pattern)
 *   - `features/calculator/anketa/AnketaFormContainer.tsx` (hydration cascade)
 *   - ADR-0025 §Q7 (fromQuote seed)
 */

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { genUuid } from '@/lib/uuid';
import { useAuthStore } from '@/lib/stores/authStore';
import type { ContractDraft, UserRegion } from '@pomoshnik/shared';

import { loadSavedQuote } from '@/features/calculator/quote/state/savedQuotesIdb';

import { ContractFormView } from './ContractFormView';
import { useFromQuoteSeeding } from './hooks/useFromQuoteSeeding';
import { contractReducer, type ContractFormState } from './state/contractReducer';
import { createInitialContractDraft, type ContractEntryMode } from './state/contractDraftDefaults';
import { loadContractDraftFromIdb, saveContractDraftToIdb } from './state/contractDraftIdbSync';
import { loadSavedContract } from './state/savedContractsIdb';
import { useContractDraftStore } from './state/useContractDraftStore';
import { mapQuoteStateToContractDraft } from './services/quoteToContractMapping';
import { loadContractTemplate } from './templates/loadContractTemplate';

const AUTOSAVE_DEBOUNCE_MS = 300;
const DEFAULT_REGION_FALLBACK: UserRegion = 'KZ';

export interface ContractFormContainerProps {
  /** UUID существующего договора (если редактирование). Иначе новый draft. */
  contractId?: string;
}

export function ContractFormContainer({ contractId }: ContractFormContainerProps) {
  // Auth region drives template key default. Fallback 'KZ' если пользователь не
  // онбордился (нелегальный flow — Phase 0 guard всегда требует onboarding —
  // но defensive чтобы Container не падал в test/preview без mocked auth).
  // DEV-warn чтобы masking onboarding bug в production-preview мог быть пойман.
  const authRegion = useAuthStore((s) => s.region);
  const region: UserRegion = authRegion ?? DEFAULT_REGION_FALLBACK;
  if (import.meta.env.DEV && authRegion === null) {
    console.warn(
      '[ContractFormContainer] auth.region is null — falling back to KZ. Onboarding likely incomplete.',
    );
  }

  // contractId stable per Container lifetime — useRef фиксирует генерацию.
  const contractIdRef = useRef(contractId ?? genUuid());
  const draftKey = contractIdRef.current;

  // Phase 6.5G A8 — entry-mode discriminator считывается из URL query при
  // mount (one-shot, stable per Container lifetime через useRef). Резолвится:
  //   - edit mode (contractId передан) → 'auto' (HYDRATE из IDB перепишет
  //     spec полностью; entryMode не имеет значения).
  //   - new draft + `?fromQuote=<non-empty>` → 'auto' (useFromQuoteSeeding
  //     HYDRATE overrides single block_cost rows seed-ом из КП). Empty value
  //     `?fromQuote=` (без UUID) treated как absent — fall through к manual
  //     default чтобы пользователь не landed на confusing single block_cost
  //     screen без подтянутых данных.
  //   - new draft, no `?fromQuote` (явный `?manual=true` либо direct URL без
  //     params) → 'manual' (17 default позиций, decision A8.4 fallback —
  //     consistency с Главная «Заполнить вручную» flow).
  const [searchParams] = useSearchParams();
  const entryModeRef = useRef<ContractEntryMode>(
    contractId !== undefined || (searchParams.get('fromQuote') ?? '').trim() !== ''
      ? 'auto'
      : 'manual',
  );

  // Initial state — pending hydration. useReducer init lazy через builder.
  const [state, dispatch] = useReducer(
    contractReducer,
    null,
    (): ContractFormState => ({
      ...createInitialContractDraft({
        region,
        id: contractIdRef.current,
        entryMode: entryModeRef.current,
      }),
      hydrationStatus: 'pending',
    }),
  );

  // Phase 3 hook — reads ?fromQuote=<uuid> from URL, loads saved quote + maps.
  const fromQuoteResult = useFromQuoteSeeding();

  // Skip autosave on the first state update after HYDRATE — иначе сразу после
  // hydration пишем `loaded state with bumped updatedAt` обратно в IDB/Zustand
  // (I/O noise; no user data lost, но bloats writes). Set to `true` инициально
  // (pre-hydration) и каждый раз когда мы dispatching HYDRATE — autosave effect
  // потребляет flag и переходит в обычный режим только после первого USER edit.
  const skipNextAutosaveRef = useRef(true);

  // Hydration cascade. Mirror Block 12 QuotePageContainer pattern.
  useEffect(() => {
    let cancelled = false;

    async function hydrate(): Promise<void> {
      // Priority 1 — Zustand cache (sync, fast).
      const fromZustand = useContractDraftStore.getState().getDraft(draftKey);
      if (fromZustand) {
        if (!cancelled) {
          skipNextAutosaveRef.current = true;
          dispatch({ type: 'HYDRATE', payload: fromZustand });
        }
        return;
      }

      // Priority 2 — durable IDB draft mirror.
      try {
        const fromIdb = await loadContractDraftFromIdb(draftKey);
        if (cancelled) return;
        if (fromIdb) {
          useContractDraftStore.getState().setDraft(draftKey, fromIdb);
          skipNextAutosaveRef.current = true;
          dispatch({ type: 'HYDRATE', payload: fromIdb });
          return;
        }
      } catch {
        // graceful: idb может быть unavailable, fall through к seed/blank
      }

      // Priority 2.5 — saved_contracts table fallback (Phase 6.5G hotfix
      // 2026-05-25). До этого hotfix Container hydration cascade читал только
      // contract-drafts IDB (P2 above). После explicit «Сохранить» в
      // BottomActions.handleSave draft IDB cleared (BottomActions.tsx clears
      // через clearContractDraftFromIdb + clearDraft Zustand) — поэтому при
      // повторном открытии договора через ContractListView → ContractCard →
      // `/contracts/:id/edit` Container fall-through к P3 (fromQuote miss, no
      // query) → P4 default city HYDRATE → пустая форма. Aibek visual verify
      // round 6 фиксировал bug «Открыл сохранённый договор → поля пустые».
      //
      // Fix: проверяем saved_contracts ПОСЛЕ draft IDB. Ordering important —
      // draft IDB takes precedence (most-recent autosave preserves edits that
      // user сделал ПОСЛЕ explicit save). saved_contracts consulted только
      // когда draft IDB пуст (cleared at save). Edge case clear-on-save
      // failure (stale draft с timestamp < saved) — rare; должен решаться
      // фиксом clearContractDraftFromIdb reliability, не workaround'ом
      // здесь.
      try {
        const fromSaved = await loadSavedContract(draftKey);
        if (cancelled) return;
        if (fromSaved) {
          // SavedContract.state.hydrationStatus отсутствует (ContractDraft
          // schema), но HYDRATE сам ставит 'ready' — payload Partial<ContractDraft>.
          useContractDraftStore
            .getState()
            .setDraft(draftKey, { ...fromSaved.state, hydrationStatus: 'ready' });
          skipNextAutosaveRef.current = true;
          dispatch({ type: 'HYDRATE', payload: fromSaved.state });
          return;
        }
      } catch {
        // graceful: idb может быть unavailable, fall through к fromQuote seed / default
      }

      // Priority 3 — fromQuote seed (Phase 3). Если URL содержит ?fromQuote=
      // hook резолвится async — ждём loading=false (effect перезапустится).
      if (fromQuoteResult.loading) return;

      if (fromQuoteResult.seed) {
        if (!cancelled) {
          skipNextAutosaveRef.current = true;
          dispatch({ type: 'HYDRATE', payload: fromQuoteResult.seed });
        }
        return;
      }

      // Priority 4 — populate defaultCity from template (если ещё не подтянули).
      const template = loadContractTemplate(state.templateKey);
      if (!cancelled) {
        skipNextAutosaveRef.current = true;
        dispatch({
          type: 'HYDRATE',
          payload: { contractCity: template.meta.defaultCity },
        });
      }
    }

    void hydrate();

    return () => {
      cancelled = true;
    };
    // Phase 6.5E Item H — state.templateKey может меняться через RegionPicker
    // (SET_CONTRACT_REGION), но hydration cascade выполняется один раз при mount;
    // state.templateKey не должен re-trigger её. eslint-disable preserved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, fromQuoteResult.loading, fromQuoteResult.seed]);

  // 300ms debounced autosave. Skip first effect run после HYDRATE (см.
  // skipNextAutosaveRef) чтобы не писать pristine echo обратно сразу после load.
  useEffect(() => {
    if (state.hydrationStatus !== 'ready') return;
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const snapshot: ContractFormState = { ...state, updatedAt: Date.now() };
      useContractDraftStore.getState().setDraft(draftKey, snapshot);
      void saveContractDraftToIdb(draftKey, snapshot);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [state, draftKey]);

  // PR-B1 (ADR-0034) — session-end clear реквизитов клиента (ПДн, P-009).
  // Триггеры: «На главную» (onBeforeNavigateHome ниже) + beforeunload (закрытие
  // приложения). НЕ триггерится на PDF/Share (перенос с ADR-0025 A9) и на
  // cross-route навигацию (/contracts, Calculator) — work in progress preserved.
  //
  // stateRef держит последний state для стабильного callback без ре-подписки
  // на beforeunload при каждом изменении state.
  const stateRef = useRef(state);
  stateRef.current = state;

  const clearCustomerRequisitesOnExit = useCallback(() => {
    const current = stateRef.current;
    if (current.preserveCustomerRequisites) return; // opt-in — сохраняем
    const cleared = contractReducer(current, { type: 'CLEAR_CUSTOMER_REQUISITES_VALUES' });
    if (cleared === current) return; // идемпотентно: уже пусто (reducer by-ref no-op)
    // dispatch для UI-консистентности, если экран ещё смонтирован (beforeunload).
    dispatch({ type: 'CLEAR_CUSTOMER_REQUISITES_VALUES' });
    // Flush очищенного снимка в Zustand + IDB — иначе hydration (Priority 1/2)
    // восстановит старые реквизиты при возврате на /contracts/:id/edit.
    const snapshot: ContractFormState = { ...cleared, updatedAt: Date.now() };
    useContractDraftStore.getState().setDraft(draftKey, snapshot);
    // .catch — A.4-симметрия с ContractPdfPreviewContainer session-end clear +
    // защита от unhandled-rejection (IDB-write может reject на teardown/app-close).
    void saveContractDraftToIdb(draftKey, snapshot).catch((err) => {
      if (import.meta.env.DEV) {
        const errName = err instanceof Error ? err.name : 'unknown';
        console.error('[ContractFormContainer] session-end flush failed', errName);
      }
    });
  }, [draftKey]);

  useEffect(() => {
    // beforeunload: app close / refresh. IDB-write best-effort (async-unsafe на
    // unload — задокументировано в ADR-0034 как известное ограничение).
    window.addEventListener('beforeunload', clearCustomerRequisitesOnExit);
    return () => window.removeEventListener('beforeunload', clearCustomerRequisitesOnExit);
  }, [clearCustomerRequisitesOnExit]);

  // PR-C2 Phase 2 (ADR-0036) — частичный пересев из изменённого КП на forward-
  // открытии. Зеркало Priority 2.6 invalidation из ADR-0027 (для КП от анкеты).
  //
  // Выполняется ОДИН раз после ready-hydration (one-shot через ref), только если
  // договор связан с КП (`fromQuoteId`) И уже был засеян (`lastQuoteUpdatedAt`).
  // Legacy/standalone договоры без `lastQuoteUpdatedAt` → skip (никаких surprise-
  // overwrite). Если `quote.updatedAt > lastQuoteUpdatedAt` → пересев + уведомление.
  // Реквизиты клиента и прочее ручное — preserve (reducer, см. ADR-0036 §4).
  // PR-C5 (ADR-0039) — подтверждаемый пересев родного блока из изменённого КП.
  // Авто-dispatch (ADR-0036) заменён модалкой «Обновить данные из КП?» Да/Нет.
  // One-shot после ready-hydration: грузим родной КП, если новее последнего сева
  // — сохраняем готовый payload (`pendingReseed`) и, если ещё не отвечали на
  // промпт для этой версии КП, открываем модалку. Ссылка «↻ Обновить из КП»
  // (Вариант 2) видна, пока КП новее последнего РЕАЛЬНОГО сева.
  const reseedCheckedRef = useRef(false);
  const [reseedNotice, setReseedNotice] = useState(false);
  const [reseedPromptOpen, setReseedPromptOpen] = useState(false);
  const [pendingReseed, setPendingReseed] = useState<{
    payload: Partial<ContractDraft>;
    quoteUpdatedAt: number;
  } | null>(null);

  useEffect(() => {
    if (state.hydrationStatus !== 'ready') return;
    if (reseedCheckedRef.current) return;

    const fromQuoteId = state.fromQuoteId;
    const lastSeeded = state.lastQuoteUpdatedAt;
    // Гард: нет связи с КП либо договор не сеялся (legacy/standalone) → skip.
    if (!fromQuoteId || lastSeeded === undefined) {
      reseedCheckedRef.current = true;
      return;
    }
    reseedCheckedRef.current = true;

    let cancelled = false;
    void (async () => {
      try {
        const quote = await loadSavedQuote(fromQuoteId);
        if (cancelled || !quote) return;
        if (quote.updatedAt <= lastSeeded) return; // КП не новее последнего сева → ничего.

        const payload: Partial<ContractDraft> = {
          ...mapQuoteStateToContractDraft(quote),
          seededFromQuoteAt: Date.now(),
        };
        if (cancelled) return;
        // Ссылка «↻» доступна (КП новее реального сева).
        setPendingReseed({ payload, quoteUpdatedAt: quote.updatedAt });
        // Модалку показываем только если для этой версии КП ещё не отвечали на
        // промпт (ни «Да», ни «Нет»): quote.updatedAt > max(сев, ответ).
        const ackFloor = Math.max(lastSeeded, stateRef.current.quotePromptAckAt ?? -1);
        if (quote.updatedAt > ackFloor) setReseedPromptOpen(true);
      } catch (err) {
        // graceful: КП не найден / IDB недоступен → skip (как остальные IDB-пути).
        if (import.meta.env.DEV) {
          const errName = err instanceof Error ? err.name : 'unknown';
          console.warn('[ContractFormContainer] re-seed check failed:', errName);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [state.hydrationStatus, state.fromQuoteId, state.lastQuoteUpdatedAt]);

  // «Да» / клик ссылки «↻ Обновить из КП» → пересев родного блока + бамп обоих
  // таймстампов (ссылка скрывается). Баннер-подтверждение после пересева.
  const handleConfirmReseed = useCallback(() => {
    const pending = pendingReseed;
    setReseedPromptOpen(false);
    if (!pending) return;
    dispatch({ type: 'PARTIAL_RESEED_CONTRACT', payload: pending.payload });
    setReseedNotice(true);
  }, [pendingReseed]);

  // «Нет» / Esc / overlay → бамп только quotePromptAckAt (модалка не вернётся для
  // этой версии КП; ссылка «↻» остаётся видна — Вариант 2).
  const handleDismissReseed = useCallback(() => {
    const pending = pendingReseed;
    setReseedPromptOpen(false);
    if (!pending) return;
    dispatch({ type: 'ACK_QUOTE_RESEED_PROMPT', ackAt: pending.quoteUpdatedAt });
  }, [pendingReseed]);

  // Ссылка «↻ Обновить из КП»: видна, пока КП новее последнего РЕАЛЬНОГО сева.
  // После «Да»/«↻» (lastQuoteUpdatedAt бампнут) → false. После «Нет» — остаётся.
  const reseedAvailable =
    pendingReseed !== null && pendingReseed.quoteUpdatedAt > (state.lastQuoteUpdatedAt ?? -1);

  if (state.hydrationStatus === 'pending') {
    return (
      <main
        className="min-h-screen flex flex-col bg-canvas items-center justify-center"
        data-testid="contract-form-hydrating"
      >
        <p className="text-ink text-base p-8" role="status">
          Загружаем форму договора…
        </p>
      </main>
    );
  }

  return (
    <ContractFormView
      state={state}
      dispatch={dispatch}
      draftKey={draftKey}
      onBeforeNavigateHome={clearCustomerRequisitesOnExit}
      reseedNotice={reseedNotice}
      onDismissReseedNotice={() => setReseedNotice(false)}
      reseedPromptOpen={reseedPromptOpen}
      onConfirmReseed={handleConfirmReseed}
      onDismissReseed={handleDismissReseed}
      reseedAvailable={reseedAvailable}
    />
  );
}
